<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const i18nScope = 'components.sales_analytics';

  const props = defineProps({
    overview: {
      type: Object,
      required: true
    },
    comparison: {
      type: Object,
      default: null
    },
    libraryName: {
      type: String,
      required: true
    }
  });

  const totalSpendFormatted = computed(() => {
    if (!props.overview?.total_spend) return '-';
    const entries = Object.entries(props.overview.total_spend);
    if (entries.length === 0) return '-';
    return entries.map(([currency, amount]) =>
      formatCurrency(amount, currency)
    ).join(' / ');
  });

  const avgLoansPerCopy = computed(() => {
    if (!props.overview?.total_purchases || props.overview.total_purchases === 0) return 0;
    return (props.overview.total_loans / props.overview.total_purchases).toFixed(1);
  });

  const peerAvgLoansPerCopy = computed(() => {
    if (!props.comparison?.avg_purchases_per_library || props.comparison.avg_purchases_per_library === 0) return 0;
    return (props.comparison.avg_loans_per_library / props.comparison.avg_purchases_per_library).toFixed(1);
  });

  const purchasesDiff = computed(() => {
    if (!props.comparison?.sufficient_data || !props.comparison?.avg_purchases_per_library) return null;
    const diff = props.overview.total_purchases - props.comparison.avg_purchases_per_library;
    const pct = ((diff / props.comparison.avg_purchases_per_library) * 100).toFixed(0);
    return { value: diff, percentage: pct };
  });

  const loansDiff = computed(() => {
    if (!props.comparison?.sufficient_data) return null;
    const myAvg = parseFloat(avgLoansPerCopy.value);
    const peerAvg = parseFloat(peerAvgLoansPerCopy.value);
    if (peerAvg === 0) return null;
    const diff = myAvg - peerAvg;
    const pct = ((diff / peerAvg) * 100).toFixed(0);
    return { value: diff.toFixed(1), percentage: pct };
  });

  function formatCurrency(amount, currency) {
    const formatter = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter.format(amount);
  }

  function getDiffClass(percentage) {
    const pct = parseInt(percentage);
    if (pct > 0) return 'text-success';
    if (pct < 0) return 'text-danger';
    return 'text-muted';
  }

  function getDiffIcon(percentage) {
    const pct = parseInt(percentage);
    if (pct > 0) return 'bi-arrow-up';
    if (pct < 0) return 'bi-arrow-down';
    return 'bi-dash';
  }
</script>

<template>
  <div class="row mb-4">
    <!-- Total Purchases -->
    <div class="col-md-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-subtitle mb-2 text-muted">{{ t(`${i18nScope}.total_purchases`) }}</h6>
          <h3 class="card-title mb-1">{{ overview.total_purchases?.toLocaleString() || 0 }}</h3>
          <div v-if="purchasesDiff" class="small" :class="getDiffClass(purchasesDiff.percentage)">
            <i :class="['bi', getDiffIcon(purchasesDiff.percentage)]"></i>
            {{ purchasesDiff.percentage }}% {{ t(`${i18nScope}.vs_peers`) }}
          </div>
          <div v-else-if="comparison" class="small text-muted">
            {{ t(`${i18nScope}.peer_avg`) }}: {{ comparison.avg_purchases_per_library?.toLocaleString() || '-' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Total Spend -->
    <div class="col-md-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-subtitle mb-2 text-muted">{{ t(`${i18nScope}.total_spend`) }}</h6>
          <h3 class="card-title mb-1">{{ totalSpendFormatted }}</h3>
        </div>
      </div>
    </div>

    <!-- Total Loans -->
    <div class="col-md-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-subtitle mb-2 text-muted">{{ t(`${i18nScope}.total_loans`) }}</h6>
          <h3 class="card-title mb-1">{{ overview.total_loans?.toLocaleString() || 0 }}</h3>
        </div>
      </div>
    </div>

    <!-- Loans per Copy -->
    <div class="col-md-3">
      <div class="card h-100">
        <div class="card-body">
          <h6 class="card-subtitle mb-2 text-muted">{{ t(`${i18nScope}.loans_per_copy`) }}</h6>
          <h3 class="card-title mb-1">{{ avgLoansPerCopy }}</h3>
          <div v-if="loansDiff" class="small" :class="getDiffClass(loansDiff.percentage)">
            <i :class="['bi', getDiffIcon(loansDiff.percentage)]"></i>
            {{ loansDiff.percentage }}% {{ t(`${i18nScope}.vs_peers`) }}
          </div>
          <div v-else-if="comparison?.sufficient_data" class="small text-muted">
            {{ t(`${i18nScope}.peer_avg`) }}: {{ peerAvgLoansPerCopy }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .card-title {
    font-weight: 600;
  }
</style>
